import { useState, useEffect, useCallback } from 'react'
import { authenticatedFetch } from '../services/api/apiClient'
import { apiEndpoints } from '../consts'
import useFetch from './useFetch'
import logger from '../utils/logger'

const DEFAULT_FILTER_OPTIONS = {
  campaigns: [],
  agents: [],
  dispositions: [],
  qaGrades: ['PASS', 'FAIL', 'TBD'],
  criteria: []
}

const DEFAULT_DATA = {
  calls: [],
  statistics: {
    totalEvaluated: 0,
    passRate: 0,
    avgScore: 0,
    avgApplicable: 0,
    mostFailedCriterion: null,
    byCampaign: [],
    byAgent: [],
    byCriteria: []
  },
  chartData: {
    trend: [],
    criteriaDistribution: []
  }
}

/**
 * Custom hook to handle QA Summary data and operations
 *
 * @returns {Object} State and methods for QA summary dashboard
 */
const useQASummaryData = () => {
  const [filters, setFilters] = useState({
    campaigns: [],
    agents: [],
    dispositions: [],
    startDate: null,
    endDate: null,
    qaGrade: null,
    failedCriteria: []
  })

  const { data: filterOptionsData, loading: loadingFilters } = useFetch(apiEndpoints.autoQaFilters, {
    initialData: null,
    transform: (result) => result.data,
    errorMessage: 'Failed to fetch filter options'
  })
  const filterOptions = filterOptionsData || DEFAULT_FILTER_OPTIONS

  const [data, setData] = useState(DEFAULT_DATA)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(25)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)

  const [sortField, setSortField] = useState('qa_evaluation_generated_at')
  const [sortDirection, setSortDirection] = useState('DESC')

  const buildQueryParams = useCallback(() => {
    const params = new URLSearchParams()

    if (filters.campaigns.length > 0) {
      params.set('campaigns', filters.campaigns.join(','))
    }
    if (filters.agents.length > 0) {
      params.set('agents', filters.agents.join(','))
    }
    if (filters.dispositions.length > 0) {
      params.set('dispositions', filters.dispositions.join(','))
    }
    if (filters.startDate) {
      params.set('startDate', filters.startDate)
    }
    if (filters.endDate) {
      params.set('endDate', filters.endDate)
    }
    if (filters.qaGrade) {
      params.set('qaGrade', filters.qaGrade)
    }
    if (filters.failedCriteria.length > 0) {
      params.set('failedCriteria', filters.failedCriteria.join(','))
    }

    params.set('page', page.toString())
    params.set('limit', limit.toString())
    params.set('sortField', sortField)
    params.set('sortDirection', sortDirection)

    return params
  }, [filters, page, limit, sortField, sortDirection])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const params = buildQueryParams()
      const response = await authenticatedFetch(`${apiEndpoints.autoQaResults}?${params.toString()}`)

      if (!response.ok) {
        throw new Error(`Failed to fetch QA results: ${response.status}`)
      }

      const result = await response.json()

      setData({
        calls: result.data.calls,
        statistics: result.data.statistics,
        chartData: result.data.chartData
      })
      setTotal(result.meta.total)
      setTotalPages(result.meta.totalPages)
    } catch (err) {
      logger.error('Error fetching QA results', { error: err.message })
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [buildQueryParams])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSort = useCallback((field) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'DESC' ? 'ASC' : 'DESC')
    } else {
      setSortField(field)
      setSortDirection('DESC')
    }
    setPage(1)
  }, [sortField])

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage)
  }, [])

  const handleLimitChange = useCallback((newLimit) => {
    setLimit(newLimit)
    setPage(1)
  }, [])

  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }))
    setPage(1)
  }, [])

  const clearFilters = useCallback(() => {
    setFilters({
      campaigns: [],
      agents: [],
      dispositions: [],
      startDate: null,
      endDate: null,
      qaGrade: null,
      failedCriteria: []
    })
    setPage(1)
  }, [])

  const hasActiveFilters = useCallback(() => {
    return (
      filters.campaigns.length > 0 ||
      filters.agents.length > 0 ||
      filters.dispositions.length > 0 ||
      filters.startDate !== null ||
      filters.endDate !== null ||
      filters.qaGrade !== null ||
      filters.failedCriteria.length > 0
    )
  }, [filters])

  const exportCSV = useCallback(async (options = {}) => {
    try {
      const {
        dateRangeOption = 'current',
        customStartDate = null,
        customEndDate = null,
        selectedCriteria = null
      } = options

      const params = buildQueryParams()
      params.delete('page')
      params.delete('limit')

      if (dateRangeOption === 'custom') {
        params.delete('startDate')
        params.delete('endDate')
        if (customStartDate) params.set('startDate', customStartDate)
        if (customEndDate) params.set('endDate', customEndDate)
      }

      if (selectedCriteria) {
        params.set('columns', JSON.stringify(selectedCriteria))
      }

      const response = await authenticatedFetch(`${apiEndpoints.autoQaResultsExport}?${params.toString()}`)

      if (!response.ok) {
        throw new Error(`Failed to export CSV: ${response.status}`)
      }

      const blob = await response.blob()

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url

      const contentDisposition = response.headers.get('Content-Disposition')
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
      link.download = filenameMatch ? filenameMatch[1] : `qa-results-export-${new Date().toISOString().split('T')[0]}.csv`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      logger.error('Error exporting CSV', { error: err.message })
      setError(err.message)
      throw err
    }
  }, [buildQueryParams])

  return {
    filters,
    filterOptions,
    loadingFilters,
    updateFilters,
    clearFilters,
    hasActiveFilters,

    data,
    loading,
    error,

    page,
    limit,
    totalPages,
    total,
    handlePageChange,
    handleLimitChange,

    sortField,
    sortDirection,
    handleSort,

    fetchData,
    exportCSV
  }
}

export default useQASummaryData
